/*
 * Copyright (c) 2024. Spin in het Web B.V. Apeldoorn NL.
 * Deze software is géén open source. Het auteursrecht van deze software
 * ligt bij Spin in het Web B.V. en/of Jellybean B.V. en/of de auteur.
 * Gebruik van deze software is onderhevig aan een licentieovereenkomst.
 * www.spininhetweb.nl
 */

/*
* sihwcache.ts
* 19-11-2024 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Injectable} from "@angular/core";
import {Sihwlog} from "../sihwlog";
import {LocalConfig} from "../localconfig";
import {cloneDeep} from "lodash-es";
import * as objectHash from 'object-hash';

@Injectable({
  providedIn: 'root'
})
export class SihwcacheService {
  private log: Sihwlog = new Sihwlog; //wordt in initservice goedgezet
  private conf = new LocalConfig();
  private maxSize: number = 100 * 1024 * 1024; //wordt ook opnieuw gezet

  private cache = new Map(); //de cache
  private currentSize = 0; //schatting in bytes
  private tagmap = new Map(); //de tags

  constructor() {
    void this.initService();
  }

   private async initService() {
      await this.conf.whenLoaded; //wacht op de config, we zijn toch async
     //nu alles goedzetten
     this.log = new Sihwlog(this.conf.$('SIHW.cache.logLevel','warn'), "SihwCache");
     this.maxSize = <number> this.conf.$('SIHW.cache.maxSize', 100 * 1024 * 1024);
     this.log.debug(`Sihwcache geladen. Maxsize: ${Math.round((this.maxSize / (1024 * 1024)) * 10) / 10} MB`);
  }
  /**
   * Haal data uit de cache. Returns undefined als niet gevonden
   * @param key De zoekkey. Kan een string zijn of een object. De hash hiervan is de interne key
   * @param [extraTags] Extra tags die aan het cache-object moeten worden toegevoegd, als hij er is
   */
  get(key: string | Object, extraTags: string[] = []): any {
    let cacheKeyHash = objectHash.sha1(key);
    this.log.debug(`sihwcache.get`,cacheKeyHash,key);
    if (this.cache.has(cacheKeyHash)) {

      let cached = this.cache.get(cacheKeyHash); //dit is een map met properties en data
      this.log.debug('CacheHit voor key', key, cacheKeyHash, cached);
      cached.set('lasthit', Date.now());

      if (extraTags && extraTags.length) {
        //nieuwe tags toevoegen
        //tags is op keys
        for (let key of extraTags) {
          if (cached.get('tags').has(key)) {
            continue; //deze kennen we al
          }
          let tagkeys;
          if (this.tagmap.has(key)) {
            tagkeys = this.tagmap.get(key);
          } else {
            tagkeys = new Set();
            this.tagmap.set(key, tagkeys);
          }
          //dkeys moet dus nu de cacheKeyHash erbij hebben
          tagkeys.add(cacheKeyHash);
          //en we houden in de cached map ook een link naar deze dkeys-set
          //zodat we snel kunnen verwijderen als de cachekey eraan gaat
          cached.get('tags').set(key, tagkeys);
        }
      }
      return cloneDeep(cached.get('data')); //en geef een kopie terug
    } else {
      this.log.debug(`Miss`);
      return undefined; //niet gevonden
    }
  }

  /**
   * Voeg data toe (of vervang bestaande data) onder de gegeven key, en met de gegeven tags
   * @param key
   * @param data
   * @param tags
   */
  public add(key: string|Object, data: any, tags: string[] = [] ): void {
    const nu = Date.now();
    let cacheKeyHash = objectHash.sha1(key); //maak een hash van de key of het object
    if (this.cache.has(cacheKeyHash)) {
      this.deleteCacheHash(cacheKeyHash); //eerst weg, ivm size
    }
    let size = this.sizeof(data); //schatting van de grootte
    this.log.debug(`sihwmemcache: addCache ${cacheKeyHash} size ${size}`);
    this.currentSize += size;
    if (this.currentSize > this.maxSize) {
      this.opruimen(); //zorg dat we weer onder de maxSize zitten
    }

    data = cloneDeep(data); //altijd een kopie

    let cached = new Map;
    cached.set('data', data);
    cached.set('tags', new Map());
    cached.set('size', size);
    cached.set('key', cacheKeyHash); //ook hier bewaren
    cached.set('lasthit', nu); //toevoegen telt ook als hit

    //nieuwe tags toevoegen
    //tagmap is op keys
    for (let key of tags) {
      if (cached.get('tags').has(key)) {
        continue; //deze kennen we al (dubbel in de keys?)
      }
      let tagkeys;
      if (this.tagmap.has(key)) {
        tagkeys = this.tagmap.get(key); //al meer cache-objecten met deze tag
      }
      else {
        tagkeys = new Set();
        this.tagmap.set(key, tagkeys);
      }
      //tagkeys moet dus nu de cacheKeyHash erbij hebben
      tagkeys.add(cacheKeyHash);
      //en we houden in de cached map ook een link naar deze tagkeys-set
      //zodat we snel kunnen verwijderen als de cachekey eraan gaat
      cached.get('tags').set(key, tagkeys);
    }
    //en toevoegen
    this.cache.set(cacheKeyHash, cached);
    this.log.debug(`Cache is nu ${this.currentSize} ${Math.round((this.currentSize / this.maxSize) * 100)}%`);
    this.log.debug(`Toevoegen aan cache ${Date.now() - nu} ms`);
  }

  /**
   * Verwijder een cacheobject met een bepaalde key
   * @param cachekey
   */
  public delete(cachekey: string|Object): void {
    this.deleteCacheHash(objectHash.sha1(cachekey));
  }

  /**
   * Clear en reinit de cache
   * @returns void
   */
  public clear(): void {
    this.log.debug('sihwcache: clearcache');
    this.cache.clear();
    this.tagmap.clear();
    this.currentSize = 0;
  }

  /**
   * Clear de cacheobjecten met de opgegeven tags
   * @param tags
   */
  public clearTags(tags: string|string[]): void {
    if (! Array.isArray(tags))
    {
      tags = <string[]> [tags];
    }
    //we voegen de "elke categorie"-tag toe
    tags = [...tags,'*'];

    let verwijderen: Set<string> = new Set();
    for (let key of tags) {
      if (this.tagmap.has(key)) {
        for(let cacheKeyHash of this.tagmap.get(key))
        {
          verwijderen.add(cacheKeyHash)
        }
      }
    }
    //en nu pas keurig stuk voor stuk verwijderen, ook ivm de size
    this.log.debug(`Clear tags ${tags.join(', ')}: ${verwijderen.size} items`);
    for(let cacheKeyHash of verwijderen)
    {
      this.deleteCacheHash(cacheKeyHash); //al op hash-niveau
    }
    this.log.debug(`Cache is nu ${this.currentSize} ${Math.round((this.currentSize / this.maxSize) * 100)}%`);
  }

  ///////////////////////// PRIVATE //////////////////////////////////
  /**
   * Verwijder een key uit de cache
   * @param cacheKeyHash Al gehashede key
   * @returns  void
   */
  private deleteCacheHash(cacheKeyHash: string) {
    if (!this.cache.has(cacheKeyHash)) {
      return; //klaar
    }
    let cached = this.cache.get(cacheKeyHash);
    this.cache.delete(cacheKeyHash);
    this.currentSize -= cached.get('size');
    //in cached hebben we tags. De values zijn rechtstreekse objectrefs naar de tagmap sets, dus daar kunnen
    //we de key gewoon uithalen, zonder zoeken
    for(let keys of cached.get('tags').values())
    {
      keys.delete(cacheKeyHash); //deze er dus uit
    }
    // this.log.debug(`Cache is nu ${this.currentSize} ${Math.round((this.currentSize / this.maxSize) * 100)}%`);
  }

  /**
   * Maak een grove schatting van de grootte van een stuk data. Veel exrapoleren voor snelheid
   * @param data
   */
  private sizeof(data: any): number {
    let size = 0;
    let todo = [data];
    let done = new Set();

    while (todo.length) {
      let o = todo.shift();
      if (done.has(o)) {
        continue; //kennen we al
      }
      if (o === null || o === undefined) {
        //4byte pointer?
        size += 4;
        continue;
      }

      switch (typeof(o)) {
        case 'boolean':
          size += 4;  //volgends de specs
          break;
        case 'number':
          size += 8; //volgens de specs
          break;
        case 'string':
          size += 2 * o.length; //16-bits
          break;
        case 'object':
          //toevoegen aan done
          done.add(o);
          //is dit een array? Dan pakken we maximaal x items en extrapoleren we het resultaat voor de snelheid
          if (Array.isArray(o)) {
            const deelgrootte = 25; //steekproefgrootte
            if (o.length <= deelgrootte) {
              todo.push(...o); //gewoon allemaal apart
            }
            else {
              let stap = Math.floor(o.length / deelgrootte);
              let deelsize = 0;
              for(let i = 0; i < deelgrootte; i++)
              {
                deelsize += this.sizeof(o[i * stap]); //leuk verdeeld
              }
              // this.log.debug(`Size van deel ${deelgrootte} van de ${o.length}: ${deelsize}`);
              let totaalsize = deelsize * (o.length / deelgrootte);
              // this.log.debug(`Totaalsize dus ${deelsize} * (${o.length} / ${deelgrootte}) = ${deelsize} * ${o.length / deelgrootte} =  ${totaalsize}`);
              size+= Math.ceil(totaalsize);
            }
          }
          else {
            //object
            //dan tellen de keys ook mee met de grootte
            size += Object.keys(o).reduce((tot,k) => tot + 2 * k.length,0); //16bit-string
            todo.push(...Object.values(o));
          }
          break;
        default:
          this.log.warn(`sihwcache sizeof - vreemd type ${typeof(o)}`,o);
          break;
      }
    }
    return size;
  }

  /**
   * Ruim op tot we weer onder maxSize zitten
   * We hebben niet een erg efficiënte manier van laatste hit, maar gaan ervan uit dat opruimen niet heel vaak nodig is
   */
  private opruimen(): void {
    this.log.debug(`Cache voor opruimen ${this.currentSize} ${this.maxSize} ${this.cache.size} ${Math.round((this.currentSize / this.maxSize) * 100)}%`);
    while ((this.currentSize > this.maxSize) && this.cache.size > 0) {
      this.log.debug('in buitenstste opruimloop');
      let oudste: any = null;
      for(let [_cacheKeyHash, cached] of this.cache)
      {
        if ((!oudste) || cached.get('lasthit') < oudste.lasthit) {
          oudste = cached;
        }
      }

      if (oudste) {
        // this.log.debug('Dit is de oudste, die gaat weg', oudste);
        this.deleteCacheHash(oudste.get('key'));
      }
    }
    this.log.debug(`Cache na opruimen ${this.currentSize} ${Math.round((this.currentSize / this.maxSize) * 100)}%`);
  }
}
